// frontend/src/pages/PrivacyPolicyPage.jsx
import { useState, useEffect } from 'react';
import { GlobalStyles } from '@bigcommerce/big-design';
import { Shield, Database, Lock, Eye, Share2, UserCheck, Mail, Menu, X } from 'lucide-react';

const colors = {
  indigo50: '#eef2ff',
  indigo100: '#e0e7ff',
  indigo600: '#4f46e5',
  indigo900: '#312e81',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  white: '#ffffff',
};

const styles = {
  page: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: colors.gray900,
    backgroundColor: colors.white,
    margin: '-2rem',
    minHeight: '100vh',
  },
  // Navigation
  nav: {
    position: 'absolute',
    top: '2rem',
    left: 0,
    right: 0,
    zIndex: 50,
    display: 'flex',
    justifyContent: 'center',
    padding: '0 2rem',
  },
  navInner: {
    display: 'flex',
    width: '100%',
    maxWidth: '80rem',
    padding: '1rem 1.5rem',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  navLink: {
    color: 'rgba(255,255,255,0.9)',
    textDecoration: 'none',
    transition: 'color 0.2s',
    cursor: 'pointer',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  navButton: {
    background: colors.white,
    color: colors.gray900,
    padding: '0.625rem 1.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    transition: 'transform 0.2s',
    textDecoration: 'none',
  },
  hamburger: {
    display: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.white,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    padding: '2rem',
  },
  mobileMenuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '3rem',
  },
  mobileMenuClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenuLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  mobileMenuLink: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.gray900,
    textDecoration: 'none',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${colors.gray100}`,
  },
  mobileMenuButton: {
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    color: colors.white,
    padding: '1rem 2rem',
    borderRadius: '9999px',
    fontSize: '1rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    marginTop: '2rem',
    textAlign: 'center',
    textDecoration: 'none',
  },
  header: {
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    padding: '10rem 2rem 4rem 2rem',
    color: colors.white,
    position: 'relative',
  },
  headerContainer: {
    maxWidth: '64rem',
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    margin: 0,
    marginBottom: '0.5rem',
  },
  headerSubtitle: {
    fontSize: '1.125rem',
    opacity: 0.9,
    margin: 0,
  },
  content: {
    maxWidth: '64rem',
    margin: '0 auto',
    padding: '3rem 2rem',
  },
  section: {
    marginBottom: '3rem',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.gray900,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  sectionIcon: {
    width: '2.5rem',
    height: '2.5rem',
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  cardTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.gray900,
    marginBottom: '0.75rem',
  },
  cardText: {
    color: colors.gray600,
    lineHeight: 1.7,
    margin: 0,
  },
  list: {
    color: colors.gray600,
    lineHeight: 1.7,
    marginTop: '1rem',
    paddingLeft: '1.25rem',
  },
  listItem: {
    marginBottom: '0.5rem',
  },
  lastUpdated: {
    fontSize: '0.875rem',
    color: colors.gray500,
    marginTop: '1rem',
  },
  footer: {
    padding: '3rem 1.5rem',
    background: colors.white,
    borderTop: `1px solid ${colors.gray200}`,
  },
  footerContainer: {
    maxWidth: '80rem',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
  },
  footerLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.gray600,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerLink: {
    color: colors.gray600,
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  footerDot: {
    color: colors.gray300,
  },
  footerCopyright: {
    fontSize: '0.75rem',
    color: colors.gray400,
    textAlign: 'center',
    margin: 0,
  },
};

function PrivacyPolicyPage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <GlobalStyles />
      <div style={styles.page}>
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuHeader}>
              <img src="/logo-checkout-fees.png" alt="Checkout Fees" style={{ height: '42px' }} />
              <button style={styles.mobileMenuClose} onClick={closeMobileMenu}>
                <X size={28} color={colors.gray900} />
              </button>
            </div>
            <div style={styles.mobileMenuLinks}>
              <a href="/#features" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Features</a>
              <a href="/#how-it-works" style={styles.mobileMenuLink} onClick={closeMobileMenu}>How It Works</a>
              <a href="/#use-cases" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Use Cases</a>
              <a href="/support" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Documentation</a>
            </div>
            <a href="/support#contact" style={styles.mobileMenuButton} onClick={closeMobileMenu}>Support</a>
          </div>
        )}

        {/* Header */}
        <header style={{
          ...styles.header,
          ...(isMobile && { padding: '8rem 3rem 3rem 3rem' })
        }}>
          {/* Navigation */}
          <nav style={styles.nav}>
            <div style={styles.navInner}>
              <a href="/">
                <img src="/logo-checkout-light.png" alt="Checkout Fees" style={{ height: '42px' }} />
              </a>

              {/* Desktop Navigation */}
              {!isMobile && (
                <>
                  <div style={styles.navLinks}>
                    <a href="/#features" style={styles.navLink}>Features</a>
                    <a href="/#how-it-works" style={styles.navLink}>How It Works</a>
                    <a href="/#use-cases" style={styles.navLink}>Use Cases</a>
                  </div>
                  <div style={styles.navRight}>
                    <a href="/support" style={styles.navLink}>Documentation</a>
                    <a href="/support#contact" style={styles.navButton}>Support</a>
                  </div>
                </>
              )}

              {/* Mobile Hamburger */}
              {isMobile && (
                <button style={{ ...styles.hamburger, display: 'block' }} onClick={() => setMobileMenuOpen(true)}>
                  <Menu size={28} color={colors.white} />
                </button>
              )}
            </div>
          </nav>

          <div style={styles.headerContainer}>
            <img src="/logo-checkout-light.png" alt="Checkout Fees" style={{ height: '48px', display: 'block', margin: '0 auto 1rem auto' }} />
            <h1 style={{
              ...styles.headerTitle,
              textAlign: 'center',
              ...(isMobile && { fontSize: '1.75rem' })
            }}>Privacy Policy</h1>
            <p style={{ ...styles.headerSubtitle, textAlign: 'center' }}>
              How we collect, use, and protect your information
            </p>
          </div>
        </header>

        {/* Content */}
        <main style={{
          ...styles.content,
          ...(isMobile && { padding: '2rem 3rem' })
        }}>
          <p style={styles.lastUpdated}>Last updated: August 15, 2026</p>

          {/* Introduction */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Shield size={20} color={colors.white} />
              </div>
              Introduction
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                Checkout Fees is developed by Prangano.com, an extension of Inova Creative Agency ("we," "our," or "us"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our BigCommerce application.
              </p>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                By installing and using Checkout Fees, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this privacy policy, please do not install or use the application.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Database size={20} color={colors.white} />
              </div>
              Information We Collect
            </h2>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Store Information</h3>
              <p style={styles.cardText}>
                When you install Checkout Fees, we collect the following information from your BigCommerce store:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Store hash (unique identifier for your store)</li>
                <li style={styles.listItem}>Store name and domain</li>
                <li style={styles.listItem}>OAuth access tokens (to communicate with BigCommerce APIs)</li>
              </ul>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                <strong>Checkout Integration:</strong> By default, our app uses BigCommerce's own standard checkout unmodified; it detects cart changes in the background and tells BigCommerce which fees apply, and BigCommerce's checkout displays them natively. Optionally, you can switch to a custom checkout script that our app installs on your store's checkout page instead, for stores that need deeper checkout customization. You can switch between these modes at any time from the app's Settings page.
              </p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Subscription & Billing Data</h3>
              <p style={styles.cardText}>
                If you subscribe to Checkout Fees, billing is handled directly by BigCommerce through its Unified Billing system; your subscription appears as a line item on your regular BigCommerce invoice. We do not collect or store your payment card details. We do store your subscription status and trial dates so the app can reflect your current plan.
              </p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Fee Configuration Data</h3>
              <p style={styles.cardText}>
                We store the fee configurations you create within the app, including:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Fee names and display names</li>
                <li style={styles.listItem}>Fee amounts (fixed or percentage)</li>
                <li style={styles.listItem}>Fee targeting rules (categories, products, brands, etc.)</li>
                <li style={styles.listItem}>Fee enabled/disabled status</li>
              </ul>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Checkout Data</h3>
              <p style={styles.cardText}>
                During checkout, we access cart and order information to calculate applicable fees. This includes:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Cart contents (products, quantities, prices)</li>
                <li style={styles.listItem}>Order subtotals and totals</li>
                <li style={styles.listItem}>Customer group information (for fee targeting)</li>
              </ul>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                <strong>Note:</strong> We do not store personal customer information such as names, addresses, or payment details. Checkout data is processed in real-time and not retained after the fee calculation is complete.
              </p>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Eye size={20} color={colors.white} />
              </div>
              How We Use Your Information
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                We use the information we collect to:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Provide, operate, and maintain the Checkout Fees application</li>
                <li style={styles.listItem}>Calculate and apply fees to customer checkouts</li>
                <li style={styles.listItem}>Store your fee configurations so they persist across sessions</li>
                <li style={styles.listItem}>Communicate with BigCommerce APIs on your behalf</li>
                <li style={styles.listItem}>Respond to support requests and troubleshoot issues</li>
                <li style={styles.listItem}>Improve and optimize the application</li>
              </ul>
            </div>
          </section>

          {/* Data Sharing */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Share2 size={20} color={colors.white} />
              </div>
              Data Sharing & Third Parties
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}><strong>BigCommerce:</strong> We communicate with BigCommerce APIs to apply fees and retrieve store/cart data</li>
                <li style={styles.listItem}><strong>Service Providers:</strong> We may use third-party services for hosting, analytics, and error tracking that process data on our behalf</li>
                <li style={styles.listItem}><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Lock size={20} color={colors.white} />
              </div>
              Data Security
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                We implement appropriate technical and organizational security measures to protect your information, including:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Encrypted data transmission (HTTPS/TLS)</li>
                <li style={styles.listItem}>Secure storage of OAuth tokens and credentials</li>
                <li style={styles.listItem}>Regular security updates and monitoring</li>
                <li style={styles.listItem}>Access controls limiting who can view store data</li>
              </ul>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <UserCheck size={20} color={colors.white} />
              </div>
              Data Retention & Your Rights
            </h2>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Data Retention</h3>
              <p style={styles.cardText}>
                We retain your store information and fee configurations for as long as the app is installed on your store. When you uninstall the app, we will delete your data within 30 days unless required to retain it for legal purposes.
              </p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Your Rights</h3>
              <p style={styles.cardText}>
                You have the right to:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Access your data stored by our application</li>
                <li style={styles.listItem}>Request correction of inaccurate data</li>
                <li style={styles.listItem}>Request deletion of your data</li>
                <li style={styles.listItem}>Uninstall the application at any time</li>
              </ul>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                To exercise any of these rights, please contact us at the email address below.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section style={{ ...styles.section, marginBottom: 0 }}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Mail size={20} color={colors.white} />
              </div>
              Contact Us
            </h2>
            <div style={{
              ...styles.card,
              background: colors.indigo50,
              border: `1px solid ${colors.indigo100}`,
              textAlign: 'center',
              padding: '2rem',
            }}>
              <p style={{ ...styles.cardText, color: colors.gray700 }}>
                If you have questions about this Privacy Policy or our data practices, please contact us at{' '}
                <a href="mailto:hello@prangano.com" style={{ color: colors.indigo600, fontWeight: 600 }}>
                  hello@prangano.com
                </a>
              </p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer style={styles.footer}>
          <div style={styles.footerContainer}>
            <img src="/logo-checkout-fees.png" alt="Checkout Fees" style={{ height: '40px' }} />
            <div style={{
              ...styles.footerLinks,
              ...(isMobile && { flexDirection: 'column', gap: '1rem' })
            }}>
              <a href="https://www.bigcommerce.com/apps/checkout-fees-by-prangano/" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>BigCommerce App Store</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/support" style={styles.footerLink}>Documentation</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/support#contact" style={styles.footerLink}>Support</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/terms" style={styles.footerLink}>Terms of Service</a>
            </div>
            <p style={styles.footerCopyright}>© 2026 Checkout Fees. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}

export default PrivacyPolicyPage;
